import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { resolveEffectiveTier } from '../config/features';
import { sessionEndReason, bearerFrom, rememberSignedOut } from '../utils/sessionEnd';
import { zoneOrDefault } from '../utils/companyDate';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// The company's IANA time zone, for components that format a date. Unlike
// useAuth it does not throw outside an AuthProvider (components rendered on
// their own in tests); there, and before a zone has arrived, it is the default.
export const useCompanyTimezone = () => {
  const context = useContext(AuthContext);
  return zoneOrDefault(context && context.timezone);
};

// The provider's value, or null outside one. For a component that can save
// the zone and wants the profile refreshed when it is mounted in the app.
export const useOptionalAuth = () => useContext(AuthContext) || null;

// Profile of the last successfully verified user. Read back only when
// /api/auth/me cannot be reached at all, so that going offline does not look
// like being signed out: ProtectedRoute redirects to /login whenever `user` is
// null, so preserving the token alone would still bounce an offline driver to
// the login screen. Holds no secret - the token itself is a separate key, and
// this is the same profile the API would return.
const CACHED_USER_KEY = 'cachedUser';

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // Corrupt or unavailable storage is not worth failing the app over.
    return null;
  }
};

const writeCachedUser = (userData) => {
  try {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
  } catch (e) {
    /* Quota or private-mode failure: the app still works, just not offline. */
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(CACHED_USER_KEY);
  } catch (e) {
    /* Nothing useful to do. */
  }
};

// A response the server actually sent is a verdict we can trust; the absence of
// one is not. `err.response` is undefined for offline, DNS failure, connection
// refused and timeouts - none of which say anything about the token.
//
// 401 is "missing or expired", 403 is "invalid" (middleware/auth.js returns
// 403 for a token that fails jwt.verify), and /api/auth/me answers 404 when the
// user row is gone. All three mean this token will never work again.
// Everything else that carries a status - a 500, a 502 from a restarting
// server, a gateway timeout - leaves the session alone, so an outage does not
// sign out every user at once.
const isRealAuthFailure = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403 || status === 404;
};

// The stored half of signing out: the token, the cached profile and the axios
// default header. Every sign-out path (logout, a rejected /me, and the response
// interceptor below) runs this and then setUser(null).
const clearStoredSession = () => {
  localStorage.removeItem('token');
  clearCachedUser();
  delete axios.defaults.headers.common['Authorization'];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  // Kept in a ref so the interceptor is installed once and still navigates
  // with the current router.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // A tab that is already open learns its session is dead from whichever
  // request comes next, not only from /me. sessionEndReason decides which
  // responses mean that; this only acts on the answer. It fires once for a
  // burst of failures because the first one clears the stored token, and
  // every later one then fails the stored-token check.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (err) => {
      const reason = sessionEndReason({
        status: err?.response?.status,
        message: err?.response?.data?.error,
        url: err?.config?.url,
        requestToken: bearerFrom(err?.config?.headers),
        storedToken: localStorage.getItem('token'),
        pathname: window.location.pathname,
      });
      if (reason) {
        console.error('Session ended by the server, signing out:', reason);
        clearStoredSession();
        setUser(null);
        rememberSignedOut(reason);
        navigateRef.current('/login', { replace: true });
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Set up axios interceptor for auth token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Track connectivity, and re-verify silently once it comes back so a session
  // that was revoked while the device was offline is picked up on reconnect.
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (localStorage.getItem('token')) {
        loadUser({ silent: true });
      }
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // `silent` keeps the reconnect re-check from flashing the full-page spinner
  // that ProtectedRoute renders while `loading` is true.
  //
  // Resolves to the profile /me returned, or null when it did not answer, so a
  // caller (the Dashboard after Checkout) can read the fresh value without
  // waiting for a re-render.
  const loadUser = async ({ silent = false } = {}) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`);
      setUser(response.data);
      writeCachedUser(response.data);
      return response.data;
    } catch (err) {
      if (isRealAuthFailure(err)) {
        console.error('Auth rejected, signing out:', err.response.status);
        clearStoredSession();
        setUser(null);
      } else {
        // Unreachable, not rejected. Keep the token and fall back to the last
        // known profile so the cached app shell stays usable.
        console.warn('Could not reach /api/auth/me; keeping cached session.', err.message);
        const cached = readCachedUser();
        if (cached) setUser(cached);
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        email,
        password,
      });

      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      writeCachedUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, userData);

      const { token, user: newUser } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);
      writeCachedUser(newUser);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    clearStoredSession();
    setUser(null);
  };

  const isOwner = () => user?.role === 'owner';
  const isAdmin = () => ['owner', 'admin'].includes(user?.role);
  const isCrewLead = () => ['owner', 'admin', 'crew_lead'].includes(user?.role);
  const isCrewLeadOnly = () => user?.role === 'crew_lead';
  const isDriver = () => user?.role === 'driver';
  const isSuperAdmin = () => user?.isSuperAdmin === true;
  // Helper for hiding customer prices (drivers and crew leads can't see prices)
  const shouldHidePrices = () => user?.role === 'driver' || user?.role === 'crew_lead';

  // Trial status helpers.
  //
  // The trial is one free storm: a window that opens at the first completed
  // stop. The server decides when it is over (trialStormOver, from login and
  // /me) and enforces it; the client only mirrors that answer.
  const TRIAL_STORM_LIMIT = 1;
  const isOnTrial = () => user?.subscriptionStatus === 'trial';
  const getStormEventsUsed = () => user?.stormEventsUsed || 0;
  // The end time is also checked here, so a page left open across the end of
  // the window stops calling the storm "in progress" before the next /me.
  const isTrialExpired = () => isOnTrial() && (
    user?.trialStormOver === true ||
    (Boolean(user?.trialStormEndsAt) && Date.now() >= new Date(user.trialStormEndsAt).getTime())
  );
  const isTrialStormInProgress = () => isOnTrial() && !isTrialExpired() && Boolean(user?.trialStormEndsAt);
  const getTrialStormEndsAt = () => user?.trialStormEndsAt || null;
  const canPerformStormActions = () => !isTrialExpired();

  // Lapsed: read-only access. Mirrors services/subscriptionLapse.js on the
  // server, which is what actually refuses new work. An ended status lapses at
  // once, whatever the paid period end says; a trial lapses when its storm is
  // over. active and past_due never lapse.
  const isSubscriptionEnded = () => ['cancelled', 'canceled', 'expired']
    .includes(String(user?.subscriptionStatus || '').toLowerCase());
  const isLapsed = () => isSubscriptionEnded() || isTrialExpired();

  // Tier helpers.
  //
  // effectiveTier, not subscriptionTier: a cancelled or expired subscription
  // still carries the tier it was bought at, so gating on the raw column hands
  // a lapsed account its paid features indefinitely. Everything that asks
  // "what can this company do" reads this one value.
  const effectiveTier = resolveEffectiveTier(
    user?.subscriptionTier,
    user?.subscriptionStatus,
    user?.subscriptionCurrentPeriodEnd
  );
  const isPro = () => ['pro', 'premium', 'enterprise'].includes(effectiveTier);
  // True while a plan is running out its notice period - paid for, already
  // cancelled, still fully usable.
  const isCancelling = () => Boolean(user?.subscriptionCancelAtPeriodEnd);

  // The company's zone (companies.timezone), from login, register or /me.
  // Everything that shows or sends a date reads it here.
  const timezone = zoneOrDefault(user?.timezone);

  const value = {
    user,
    timezone,
    loading,
    error,
    isOffline,
    login,
    register,
    logout,
    isOwner,
    isAdmin,
    isCrewLead,
    isCrewLeadOnly,
    isDriver,
    isSuperAdmin,
    shouldHidePrices,
    refreshUser: loadUser,
    // Trial helpers
    isOnTrial,
    getStormEventsUsed,
    isTrialExpired,
    isTrialStormInProgress,
    getTrialStormEndsAt,
    canPerformStormActions,
    TRIAL_STORM_LIMIT,
    // Lapsed helpers
    isSubscriptionEnded,
    isLapsed,
    // Tier helpers
    isPro,
    effectiveTier,
    isCancelling,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
